"use client";

import { Suspense, useEffect, useState, useMemo } from "react";
import { useRouter, usePathname } from "next/navigation";
import { LayoutDashboard, Store, Settings, MessageSquare, Bug, Inbox } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { ConfirmProvider } from "@/components/ui/confirm-modal";
import {
  PanelProvider,
  useSubdomainBasePath,
} from "@/contexts/panel-context";

export default function AdminLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Suspense>
      <AdminLayoutContent>{children}</AdminLayoutContent>
    </Suspense>
  );
}

function AdminLayoutContent({ children }: { children: React.ReactNode }) {
  const [adminRoles, setAdminRoles] = useState<string[]>([]);
  const [authChecked, setAuthChecked] = useState(false);
  const [newLeads, setNewLeads] = useState(0);
  const router = useRouter();
  const basePath = useSubdomainBasePath("gestion");
  const rootPath = basePath || "/";
  const pathname = usePathname();

  const isLoginPath = pathname === "/login" || pathname === `${basePath}/login`;
  const isPublicPath =
    isLoginPath ||
    pathname === "/accept-invite" ||
    pathname === `${basePath}/accept-invite` ||
    pathname === "/reset-password" ||
    pathname === `${basePath}/reset-password`;

  useEffect(() => {
    async function load() {
      try {
        const r = await fetch("/api/gestion/me");
        if (!r.ok) {
          if (!isPublicPath) router.replace(`${basePath}/login`);
          else setAuthChecked(true);
          return;
        }
        const data = await r.json();
        const roles: string[] = data.adminRoles ?? [];

        // Already logged in and trying to access login → redirect to home
        if (isLoginPath) {
          const admin = roles.includes("MASTER") || roles.includes("ADMINISTRATOR");
          if (admin) router.replace(rootPath);
          // SALES quedó sin sección propia (Prospectos se eliminó): va a
          // tickets como SUPPORT hasta que la gestión comercial viva en
          // los comercios publicados.
          else if (roles.includes("SALES") || roles.includes("SUPPORT")) router.replace(`${basePath}/tickets`);
          else setAuthChecked(true);
          return;
        }

        if (isPublicPath) {
          setAuthChecked(true);
          return;
        }

        setAdminRoles(roles);
        setAuthChecked(true);

        const admin = roles.includes("MASTER") || roles.includes("ADMINISTRATOR");
        if (!admin) {
          const allowedPrefixes: string[] = [];
          if (roles.includes("SALES") || roles.includes("SUPPORT"))
            allowedPrefixes.push(`${basePath}/tickets`, `${basePath}/bugs`);
          const isAllowed = allowedPrefixes.some((p) => pathname === p || pathname.startsWith(p + "/"));
          if (!isAllowed && allowedPrefixes.length > 0) {
            router.replace(`${basePath}/tickets`);
          }
        }
      } catch {
        if (!isPublicPath) router.replace(`${basePath}/login`);
        else setAuthChecked(true);
      }
    }
    load();
  }, [router, basePath, rootPath, pathname]);

  const isAdmin = adminRoles.includes("MASTER") || adminRoles.includes("ADMINISTRATOR");
  const isSupport = adminRoles.includes("SUPPORT");
  const canSeeBugs = isAdmin || isSupport;

  // Contador de leads nuevos para el badge del nav + push self-heal:
  // si este navegador ya dio permiso, re-sincroniza la suscripción de
  // admin en cada entrada al panel (los endpoints caducos se recrean).
  useEffect(() => {
    if (!authChecked || !isAdmin || pathname.includes("/login")) return;
    fetch("/api/leads?status=new")
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setNewLeads(Array.isArray(rows) ? rows.length : 0))
      .catch(() => {});
    if ("Notification" in window && Notification.permission === "granted") {
      import("@/lib/push-notifications").then(({ ensurePushSubscription }) =>
        ensurePushSubscription("admin"),
      );
    }
    // pathname: refresca el contador al navegar (ej. tras marcar
    // contactado en /leads).
  }, [authChecked, isAdmin, pathname]);

  const navGroups = useMemo(() => {
    // Fusión Comercios/Directorio (F4): UNA sección "Comercios" cuya
    // lista maestra es el directorio (/comercios). "Tiendas" son las
    // cuentas Merchant (demos, invitados) y "Estadísticas" la analytics
    // del directorio. El vínculo tienda↔comercio vive en los detalles.
    // La unidad es el Comercio; la tienda es un atributo (badge en la
    // lista + card en el detalle). No hay lista de "Tiendas" separada.
    const comerciosChildren = [
      { href: `${basePath}/comercios`, label: "Listado" },
      { href: `${basePath}/directorio`, label: "Estadísticas" },
      { href: `${basePath}/merchants/archivadas`, label: "Tiendas archivadas" },
    ];

    const items = [
      ...(isAdmin ? [{ href: rootPath, label: "Dashboard", icon: LayoutDashboard }] : []),
      ...(isAdmin
        ? [{
            href: `${basePath}/comercios`,
            label: "Comercios",
            icon: Store,
            children: comerciosChildren,
            activeMatch: (p: string) =>
              p.startsWith(`${basePath}/directorio`) ||
              p.startsWith(`${basePath}/comercios`) ||
              p.startsWith(`${basePath}/merchants`),
          }]
        : []),
      ...(isAdmin
        ? [{
            href: `${basePath}/leads`,
            label: "Leads",
            icon: Inbox,
            ...(newLeads > 0 ? { badge: newLeads } : {}),
          }]
        : []),
      ...(isAdmin || isSupport
        ? [{ href: `${basePath}/tickets`, label: "Soporte", icon: MessageSquare }]
        : []),
      ...(canSeeBugs
        ? [{ href: `${basePath}/bugs`, label: "Incidencias", icon: Bug }]
        : []),
      ...(isAdmin ? [{ href: `${basePath}/config`, label: "Configuración", icon: Settings }] : []),
    ];

    return [{ label: "Navegación", items }];
  }, [isAdmin, isSupport, canSeeBugs, basePath, rootPath, newLeads]);

  // Avoid flashing the AppShell before the session is verified.
  if (!authChecked && !isPublicPath) {
    return (
      <PanelProvider basePath={basePath}>
        <head>
          <meta name="robots" content="noindex, nofollow" />
        </head>
        <div className="min-h-dvh bg-background" />
      </PanelProvider>
    );
  }

  return (
    <PanelProvider basePath={basePath}>
      <head>
        <meta name="robots" content="noindex, nofollow" />
      </head>
      <AppShell
        navGroups={navGroups}
        brandSubtitle="Admin"
        basePath={rootPath}
        loginPath={`${basePath}/login`}
        skipLayoutPaths={[`${basePath}/reset-password`, `${basePath}/accept-invite`]}
        contentMaxWidth={/\/bugs\/\d/.test(pathname) ? "max-w-6xl" : "max-w-4xl"}
      >
        <ConfirmProvider>{children}</ConfirmProvider>
      </AppShell>
    </PanelProvider>
  );
}
